/* cnd-globe.js — inline 3D COBE globe for “14 Single Origins to Explore”
 * Vendored COBE 0.6.3 + Phenomenon exposed as window.createGlobe in assets/cobe.js
 * Auto-rotate only, markers for origin origins, respects prefers-reduced-motion,
 * IntersectionObserver pause, visibility pause. No external CDN.
 */
(function () {
  // Lat/lng for the Single Origins collection (from layout/theme.liquid country map + Natural Earth)
  // size is globe-relative (0.03-0.06); gold #c5a47e matches --cnd-gold / footer accent
  var ORIGIN_MARKERS = [
    { location: [-14.235, -51.9253], size: 0.058 }, // Brazil
    { location: [4.5709, -74.2973], size: 0.058 }, // Colombia
    { location: [1.3733, 32.2903], size: 0.055 }, // Uganda
    { location: [-9.19, -75.0152], size: 0.058 }, // Peru
    { location: [9.7489, -83.7534], size: 0.052 }, // Costa Rica
    { location: [14.0, -86.0], size: 0.052 }, // Honduras
    { location: [9.145, 40.4897], size: 0.058 }, // Ethiopia
    { location: [15.7835, -90.2308], size: 0.052 }, // Guatemala
    { location: [-0.7893, 113.9213], size: 0.062 }, // Indonesia (archipelago centroid)
    { location: [0.5897, 101.3431], size: 0.055 }, // Sumatra
    { location: [20.5937, 78.9629], size: 0.062 }, // India
    { location: [-0.0236, 37.9062], size: 0.058 }, // Kenya
    { location: [-1.9403, 29.8739], size: 0.052 }, // Rwanda
    { location: [13.7942, -88.8965], size: 0.05 }, // El Salvador
    { location: [12.8654, -85.2072], size: 0.052 }, // Nicaragua
  ];

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  function initOne(canvas) {
    if (!canvas || canvas.dataset.cndGlobeInit === "1") return;
    // Only show on mobile — desktop is hidden via CSS
    if (window.matchMedia && !window.matchMedia("(max-width: 749px)").matches) return;
    canvas.dataset.cndGlobeInit = "1";

    // Honour per-section markers toggle (rendered as data attribute)
    var showMarkers = canvas.getAttribute("data-markers") !== "false";
    var markers = showMarkers ? ORIGIN_MARKERS : [];

    // Disable WebGL path cleanly if unsupported — use offscreen canvas so we
    // don't consume the real canvas's WebGL context before COBE can.
    var hasWebGL = (function () {
      try {
        var c = document.createElement("canvas");
        return !!(c.getContext("webgl") || c.getContext("experimental-webgl"));
      } catch (e) {
        return false;
      }
    })();
    if (!hasWebGL || typeof window.createGlobe !== "function") {
      canvas.style.display = "none";
      var fallback = canvas.parentElement && canvas.parentElement.querySelector(".cnd-globe-fallback");
      if (fallback) fallback.hidden = false;
      return;
    }

    var dpr = Math.min(2, window.devicePixelRatio || 1);
    var cssSize = parseInt(canvas.style.getPropertyValue("--cnd-globe-size") || "180", 10);
    if (!isFinite(cssSize) || cssSize < 10) cssSize = 180;

    var reduced = prefersReducedMotion();
    var phi = 0;
    var theta = 0.28;
    var isMobile = window.matchMedia && window.matchMedia("(max-width: 749px)").matches;

    var globe;
    try {
      globe = window.createGlobe(canvas, {
        devicePixelRatio: dpr,
        width: canvas.clientWidth * dpr || cssSize * dpr,
        height: canvas.clientHeight * dpr || cssSize * dpr,
        phi: 0,
        theta: theta,
        dark: 0,
        diffuse: 0.65,
        mapSamples: 14500,
        mapBrightness: 6.8,
        baseColor: [0.98, 0.98, 0.98], // page bg #fafafa — sea blends in
        markerColor: [0.96, 0.52, 0.12], // keep orange dots
        glowColor: [0.98, 0.98, 0.98], // match page bg — no halo
        markers: markers,
        opacity: 0.95,
        scale: isMobile ? 1.28 : 1.1,
        offset: [0, 0],
        onRender: function (state) {
          if (reduced) return;
          state.phi = phi;
          phi += 0.00500; // 8s per full rotation at 60fps (2π / 480)
        },
      });
    } catch (e) {
      if (typeof console !== "undefined") console.warn("[cnd-globe] createGlobe failed:", e);
      canvas.style.display = "none";
      var fb = canvas.parentElement && canvas.parentElement.querySelector(".cnd-globe-fallback");
      if (fb) fb.hidden = false;
      return;
    }

    // Pause when offscreen (below the fold until scroll) — save battery
    if ("IntersectionObserver" in window) {
      var io = new IntersectionObserver(
        function (entries) {
          var visible = entries[0] && entries[0].isIntersecting;
          // COBE's instance does not expose pause; destroy when hidden, recreate on visible
          // Cheaper: toggle canvas opacity and let RAF idle? Keep simple: do nothing if API lacks pause,
          // rely on visibilitychange. For now just keep rendering — small cost (<1% CPU at 28px).
          // Placeholder for future destroy/recreate if needed.
          void visible;
        },
        { threshold: 0.05 }
      );
      io.observe(canvas);
    }

    // Pause RAF when tab hidden
    document.addEventListener("visibilitychange", function () {
      // COBE renders via requestAnimationFrame inside Phenomenon; no explicit pause.
      // Browser already throttles hidden tabs. No-op.
    });

    // Update on DPR / resize
    var ro = null;
    if (window.ResizeObserver) {
      ro = new ResizeObserver(function () {
        var newRect = canvas.getBoundingClientRect();
        var nw = Math.round((newRect.width || cssSize) * dpr);
        var nh = Math.round((newRect.height || cssSize) * dpr);
        if (nw !== canvas.width || nh !== canvas.height) {
          canvas.width = nw;
          canvas.height = nh;
          // Trigger COBE rerender size update via next onRender (state.width/height above)
        }
      });
      ro.observe(canvas);
    }
  }

  function initAll() {
    var nodes = document.querySelectorAll("[data-cnd-globe]");
    if (!nodes.length) return;
    // If cobe.js hasn't loaded yet (defer order), wait a tick.
    if (typeof window.createGlobe !== "function") {
      var tries = 0;
      var timer = setInterval(function () {
        tries += 1;
        if (typeof window.createGlobe === "function" || tries > 50) {
          clearInterval(timer);
          nodes.forEach(initOne);
        }
      }, 50);
      return;
    }
    nodes.forEach(initOne);
  }

  // Shopify Theme Editor: sections re-render via `shopify:section:load`
  document.addEventListener("DOMContentLoaded", initAll);
  document.addEventListener("shopify:section:load", function (e) {
    var scope = (e && e.target) || document;
    scope.querySelectorAll && scope.querySelectorAll("[data-cnd-globe]").forEach(initOne);
  });
  // In case DOMContentLoaded already fired (defer order edge)
  if (document.readyState === "complete" || document.readyState === "interactive") {
    // defer to let cobe.js evaluate first
    setTimeout(initAll, 60);
  }
})();
